import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import Divider from '@mui/material/Divider';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import MuiTableRow from '@mui/material/TableRow';
import OverflowMenu from 'shared/components/overflow-menu/overflow-menu';
import TableHeader from 'shared/components/table/components/table-header/table-header';
import TableRow from 'shared/components/table/components/table-row/table-row';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';

import { Props, useTable } from './hooks';

import './table.scss';

const Table = <DataType extends object, Key extends NestedKeyof<DataType>>(props: Readonly<Props<DataType, Key>>): React.JSX.Element => {
  const { t } = useTranslation('table');
  const {
    orderBy,
    orderDirection,
    isOverflowMenuOpen,
    anchorEl,
    clickedRow,
    tableContainerRef,
    selectedRowsState: { state: selectedRowsState, setSelectedRowsState },
    handleRequestSort,
    getData,
    mapMenuItems,
    closeOverflowMenu,
    handleChangePage,
    handleChangeRowsPerPage,
    onSelectAllRowsChanged,
    onMoreClick,
  } = useTable<DataType, Key>(props);

  const { columns, overflowMenuOptions, rowsAreSelectable, data, className, pagination, sx, rowIdentifier, customFirstRow } = props;

  const renderDivider = (): React.JSX.Element => (
    <MuiTableRow>
      {columns.map((column) => (
        <TableCell key={`column-divider-${column.key}`} className="divider-cell">
          <Divider className="divider" />
        </TableCell>
      ))}
      {overflowMenuOptions && (
        <TableCell className="divider-cell">
          <Divider className="divider" />
        </TableCell>
      )}
      {rowsAreSelectable && (
        <TableCell className="divider-cell">
          <Divider className="divider" />
        </TableCell>
      )}
    </MuiTableRow>
  );

  return (
    <>
      <TableContainer className={className ? `${className} table-container` : 'table-container'} ref={tableContainerRef}>
        <MuiTable
          stickyHeader
          aria-label="sticky table"
          sx={{
            'tr:nth-of-type(even) td:first-of-type': {
              paddingLeft: 1,
            },
            ...sx,
          }}
        >
          <TableHeader
            {...props}
            orderDirection={orderDirection}
            orderBy={orderBy.toString()}
            onRequestSort={handleRequestSort}
            hasOverflowMenu={!!overflowMenuOptions}
            onSelectAllRowsChanged={onSelectAllRowsChanged}
          />
          <TableBody>
            {renderDivider()}
            {customFirstRow && (
              <>
                <MuiTableRow className="no-hover-table-row">
                  <TableCell className="table-cell" colSpan={rowsAreSelectable ? columns.length + 1 : columns.length} sx={{ padding: 0 }}>
                    {customFirstRow}
                  </TableCell>
                </MuiTableRow>
                {renderDivider()}
              </>
            )}
            {data.length <= 0 && (
              <MuiTableRow className="no-hover-table-row">
                <TableCell
                  className="table-cell"
                  data-testid="no-items-table-row"
                  colSpan={rowsAreSelectable ? columns.length + 1 : columns.length}
                  align={'center'}
                >
                  {t('noItemsText')}
                </TableCell>
              </MuiTableRow>
            )}
            {data.length > 0 &&
              getData().map((row, rowIndex) => (
                <Fragment key={`table-row-${rowIdentifier(row.data)}`}>
                  <TableRow
                    {...props}
                    row={row}
                    rowIndex={rowIndex}
                    clickedRow={clickedRow}
                    onMoreClick={onMoreClick}
                    selectedRowsState={selectedRowsState}
                    setSelectedRowsState={setSelectedRowsState}
                  />
                  {renderDivider()}
                </Fragment>
              ))}
          </TableBody>
        </MuiTable>
      </TableContainer>
      {pagination && (
        <TablePagination
          data-testid="table-paginator"
          className="table-paginator"
          rowsPerPageOptions={[25, 50, 100]}
          onRowsPerPageChange={handleChangeRowsPerPage}
          component="div"
          count={pagination.count}
          rowsPerPage={pagination.rowsPerPage}
          labelRowsPerPage={t('rowsPerPage')}
          labelDisplayedRows={(paginationInfo) => `${paginationInfo.from}-${paginationInfo.to} ${t('of')} ${paginationInfo.count}`}
          getItemAriaLabel={(type) => t(`${type}Pagination`)}
          page={pagination.page}
          onPageChange={handleChangePage}
          showFirstButton
          showLastButton
        />
      )}
      {overflowMenuOptions && clickedRow && (
        <OverflowMenu
          isOpen={isOverflowMenuOpen}
          onClose={closeOverflowMenu}
          anchorEl={anchorEl}
          menuItems={mapMenuItems(overflowMenuOptions, clickedRow)}
        />
      )}
    </>
  );
};

export default Table;
